import languageList from "@bt/locales/languages.json" with { type: "json" };
import { DATA_LANGUAGES, type DataLanguage } from "@bt/parts-core";
import { z } from "zod";

/**
 * The app's languages are data: `packages/locales/languages.json` lists them, and each has a
 * folder of strings there. Nothing in the code names one, except English as the last fallback and
 * German where data has it inline (DATA_LANGUAGES). ADR 0020.
 */

/** How far a language is; `draft` is offered only where the host shows drafts. */
export const LANGUAGE_STATUSES = ["draft", "community", "reviewed", "complete"] as const;
export type LanguageStatus = (typeof LANGUAGE_STATUSES)[number];

export interface LanguageInfo {
  /** A BCP 47 tag ("de", "de-AT"), the name of its folder. */
  code: string;
  /** The language in its own name, for pickers. */
  name: string;
  status: LanguageStatus;
}

/** A code from the list. Stored values are checked with `isLanguage` where they are read. */
export type Language = string;

export const LANGUAGE_LIST: readonly LanguageInfo[] = languageList as LanguageInfo[];
export const LANGUAGES: readonly Language[] = LANGUAGE_LIST.map((l) => l.code);

/** The language every chain ends in, and whose strings every other language falls back to. */
export const FALLBACK_LANGUAGE: Language = "en";

export function isLanguage(value: unknown, list = LANGUAGE_LIST): value is Language {
  return typeof value === "string" && list.some((l) => l.code === value);
}

export const languageSchema = z.string().refine((value) => isLanguage(value), "Unknown language");

/** The languages to offer: all but drafts, unless the host shows them. */
export function offeredLanguages(drafts: boolean, list = LANGUAGE_LIST): readonly LanguageInfo[] {
  return drafts ? list : list.filter((l) => l.status !== "draft");
}

/**
 * Where a key missing in `code` is looked up, in order: "de-AT" → de-AT, de, en. Only languages
 * in the list count, and English always comes last.
 */
export function fallbackChain(code: Language, list = LANGUAGE_LIST): Language[] {
  const parts = code.split("-");
  const chain: Language[] = [];
  for (let n = parts.length; n > 0; n--) {
    const candidate = parts.slice(0, n).join("-");
    if (isLanguage(candidate, list) && !chain.includes(candidate)) chain.push(candidate);
  }
  if (!chain.includes(FALLBACK_LANGUAGE)) chain.push(FALLBACK_LANGUAGE);
  return chain;
}

function canonical(tag: string): string | null {
  try {
    return Intl.getCanonicalLocales(tag)[0] ?? null;
  } catch {
    return null;
  }
}

/**
 * The language to show for what a browser or a request asks for (`navigator.languages`,
 * `Accept-Language`, in order of preference): for each tag, the language itself, then its base
 * ("de-CH" → de); English when none matches.
 */
export function matchLanguage(
  tags: readonly string[],
  options: { drafts?: boolean; list?: readonly LanguageInfo[] } = {},
): Language {
  const offered = offeredLanguages(options.drafts ?? false, options.list ?? LANGUAGE_LIST);
  const find = (tag: string) => offered.find((l) => l.code.toLowerCase() === tag.toLowerCase());
  for (const raw of tags) {
    const tag = canonical(raw.trim());
    if (!tag) continue;
    const match = find(tag) ?? find(tag.split("-")[0] ?? tag);
    if (match) return match.code;
  }
  return FALLBACK_LANGUAGE;
}

/**
 * The language of data written in English and German only (main groups, model names, the
 * built-in example data): German for German and its regional variants, English for the rest.
 */
export function dataLanguageOf(language: Language): DataLanguage {
  const base = language.split("-")[0];
  return DATA_LANGUAGES.find((l) => l === base) ?? "en";
}
